import { useRef, useState, useMemo } from 'react';
import { makeStyles, Menu, MenuItem, IconButton, Box, Tooltip } from "@material-ui/core";
import UtilService from '../../../../../../helper/service';
import { useStylesTooltip } from "../../../../../../config/theme";
import AnalyzeManualNoteBtn from '../../../../../atoms/creator/analyzeManualNoteBtn';
import useWindowSize from '../../../../../../helper/hooks/useWindowSize';
import { useDispatch, useSelector } from 'react-redux';
import { updateOnBoardingSettings } from '../../../../../../store/actions/creator/creatorOnBoarding';

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    marginTop: 164,
    minHeight: 48,
    paddingTop: 3,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #D9D9D9',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1001,
    '@media (max-width: 1320px)': {
      marginTop: 160,
    },
  },
  menuItem: {
    padding: 0,
    '& .MuiPaper-root': {
      textDecoration: 'none'
    }
  },
  graphType: {
    fontSize: 14,
    fontFamily: 'roboto-bold',
    color: '#017AEF',
    marginLeft: 22,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row'
  },
  filter: {
    fontSize: 14,
    fontFamily: 'roboto-bold',
    marginLeft: 30,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginRight: 10
    }
  },
  btn: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#697683',
    textTransform: 'unset',
    marginRight: 20,
    marginLeft: 20,
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  iconBtn: {
    width: 40,
    borderRadius: 10,
    '&:hover': {
      background: '#017AEF14'
    },
  },
  icon: {
    width: 14,
    height: 14
  },
  zoomActive: {
    alignItems: 'flex-end',
    marginRight: 90,
    '& img': {
      padding: '4px 0',
    }
  }
}));

function Module4Slide2SubHeader() {

  const classes = useStyles();
  const tooltipClasses = useStylesTooltip();
  const anchorRef = useRef(null);
  const dispatch = useDispatch();

  const [openLeftMenu, setOpenLeftMenu] = useState(false);
  const [filter, setFilter] = useState(false);

  const { module4Settings } = useSelector((state: any) => state.creatorOnBoarding);
  const { analyseStart, analyseEnd, graphZoom, graphType } = module4Settings;

  function getResponsiveWidth(width: any) {
    let customWidth = 600
    if (width > 319) { customWidth = width - 150 }
    if (width > 500) { customWidth = width - 200 }
    if (width > 600) { customWidth = width - 200 }
    if (width > 700) { customWidth = width - 250 }
    if (width > 900) { customWidth = width - 420 }
    if (width > 1200) { customWidth = width - 600 }
    return customWidth
  }

  const session_type  = 'ASYNC';
  const stimulus = {
    stimulus_video_length: 200
  }

  const headerOptions = useMemo(() => {
    if (stimulus && stimulus.stimulus_video_length < 121 && session_type === 'ASYNC') {
      return ['Immersion']
    } else {
      return ['Immersion', 'Safety', 'Immersion & Safety'
    ]
    }
  }, [stimulus])

  const { width } = useWindowSize();
  const paneWidth = getResponsiveWidth(width);

  const graphLength = useMemo(() => {
    if (stimulus && session_type === 'ASYNC') return stimulus.stimulus_video_length;
    return analyseEnd - analyseStart
  }, [stimulus, analyseEnd, analyseStart]);

  const handleMenuLeftOpen = () => {
    setOpenLeftMenu(true);
  };

  const handleMenuLeftClose = () => {
    setOpenLeftMenu(false);
  };

  const handleGraphType = (type: number) => {
    dispatch(updateOnBoardingSettings('graphType', type))
    handleMenuLeftClose();
  };

  const handleFilter = () => {
    setFilter(!filter)
  };

  const onZoomWidth = (zoomable: boolean) => {
    return false;
    const maxZoom = Math.sqrt(graphLength * 15 / paneWidth);
    if (!zoomable && graphZoom < Math.ceil(maxZoom)) {
      dispatch(updateOnBoardingSettings('graphZoom', maxZoom ))
    }
    if (!zoomable && graphZoom < Math.floor(maxZoom)) {
      const zoom = Math.ceil(graphZoom) + 1
      dispatch(updateOnBoardingSettings('graphZoom', zoom > maxZoom ? maxZoom : zoom))
    }
    if (zoomable && graphZoom >= 2) {
      dispatch(updateOnBoardingSettings( 'graphZoom', Math.ceil(graphZoom) - 1 ))
    }
    if (zoomable && graphZoom > 1 && graphZoom < 2) {
      dispatch(updateOnBoardingSettings('graphZoom', 1 ))
    }
  };

  return (
    <div className={classes.root} style={{ paddingTop: 3, minHeight: 48 }}>
      <div className={classes.graphType} ref={anchorRef} onClick={handleMenuLeftOpen}>
        {graphType === 0 && <img src={'/assets/icons/liveanalysis/header-immersion-icon.svg'} alt={'Arrow'} />}
        {graphType !== 0 && <img src={'/assets/icons/liveanalysis/header-safety-icon.svg'} alt={'Arrow'} style={{ width: 15, height: 16 }} />}
        <div style={{ whiteSpace: 'nowrap', marginLeft: 4 }}>{width > 600 ? UtilService.getSubHeader(graphType) : ''}</div>
        <img src={'/assets/icons/liveanalysis/graph-toggle-arrow.svg'} alt={'Arrow'} style={{ marginLeft: 10 }} />
      </div>

      <div
        className={classes.filter}
        onClick={() => handleFilter()}
        style={filter ? { color: '#017AEF' } : { color: '#697683' }}
      >
        <img src={`/assets/icons/liveanalysis/${filter ? `filter-select-icon.svg` : `filter-unselect-icon.svg`}`}
          alt={'Arrow'} />
        {width > 600 ? 'Filters' : ''} 
      </div>

      <Box display='flex' flex={1} />

      <div className={classes.zoomActive}>
        <Tooltip classes={tooltipClasses} title="Zoom In" placement="bottom" arrow>
          <IconButton
            className={classes.iconBtn}
            color="primary"
            size='small'
            disableRipple
            onClick={() => onZoomWidth(true)}
          >
            <img
              src={'/assets/icons/liveanalysis/zoom-in-active-btn.svg'}
              alt='img'
            />
          </IconButton>
        </Tooltip>

        <Tooltip classes={tooltipClasses} title="Zoom Out" placement="bottom" arrow>
          <IconButton
            className={classes.iconBtn}
            color="primary"
            size='small'
            disableRipple
            onClick={() => onZoomWidth(false)}
          >
            <img
              src={'/assets/icons/liveanalysis/zoom-out-inactive-btn.svg'}
              alt='img'
              style={{ padding: '4px 0' }}
            />
          </IconButton>
        </Tooltip>

        {width > 600 && <Tooltip classes={tooltipClasses} title="Clip" placement="bottom" arrow>
          <IconButton
            className={classes.iconBtn}
            color="primary"
            size='small'
            disableRipple
            style={{ marginLeft: 20 }}
          >
            <img src={`/assets/icons/liveanalysis/header-scissors-grey.svg`} alt='img' />
          </IconButton>
        </Tooltip>}

        {width > 600 && <AnalyzeManualNoteBtn />}

        {width > 600 && <Tooltip classes={tooltipClasses} title="Export Data" placement="bottom" arrow>
          <IconButton
            className={classes.iconBtn}
            color="primary"
            size='small'
            disableRipple
            style={{ marginLeft: 20 }}
          >
          <img src={'/assets/icons/download_icon.svg'} alt='img' />
          </IconButton>
        </Tooltip>}

      </div>

      <Menu
        anchorEl={anchorRef.current}
        elevation={1}
        onClose={handleMenuLeftClose}
        open={openLeftMenu}
        PaperProps={{
          style: { width: 'auto', marginTop: 50, borderRadius: 12 }
        }}
      >
        {headerOptions.map((option, index) => (
          <MenuItem
            className={classes.menuItem}
            key={option}
            onClick={() => handleGraphType(index)}
          >
            <p className={classes.btn}>{option}</p>
          </MenuItem>
        ))}
      </Menu>

    </div>
  )
}

export default Module4Slide2SubHeader;